import { atom, computed } from 'nanostores';
import { pricingStore } from './pricing';
import { proPricingPlansStore } from './proPlans';
import { teamPricingPlansStore } from './teamPlans';

import type { PricingCardActionId, PricingWorkspace } from './types';
import { workspacesStore } from './workspaces';
import { hmrPropertyFactory } from '~/utils/hmr';

const hmr = hmrPropertyFactory('FreePlanStore');

export class FreePlanStore {
  @hmr()
  isLoading = atom<boolean>(false);

  @hmr()
  actions = computed([workspacesStore.selectedWorkspace], (workspace) => {
    return getFreePricingCardActions({ workspace });
  });

  private _startRequest(id: PricingCardActionId) {
    pricingStore.startRequestForPricing('free', id);
  }

  private _endRequest() {
    pricingStore.endRequestForPricing();
  }

  async downgradeToFreePlan() {
    const workspace = workspacesStore.selectedWorkspace.get();

    this.isLoading.set(true);

    this._startRequest('cancel-subscription');

    if (workspace.type === 'personal') {
      await proPricingPlansStore.manageSubscription();
    }

    if (workspace.type === 'team') {
      await teamPricingPlansStore.manageSubscription();
    }

    this.isLoading.set(false);
    this._endRequest();
  }

  async renewSubscription(workspace: PricingWorkspace) {
    this.isLoading.set(true);
    this._startRequest('renew-subscription');

    if (workspace.type === 'personal') {
      await proPricingPlansStore.renewSubscription();
    }

    if (workspace.type === 'team') {
      await teamPricingPlansStore.renewSubscription();
    }

    this.isLoading.set(false);
    this._endRequest();
  }
}

export const freePlanStore = new FreePlanStore();

if (import.meta.env.DEV) {
  Object.assign(globalThis, { freePlanStore });
}

function getFreePricingCardActions({ workspace }: { workspace: PricingWorkspace }): FreePricingCardAction[] {
  if (workspace.type === 'team') {
    return [
      {
        type: 'disabled',
        reason: 'You’re on Teams',
      },
    ];
  }

  if (workspace.type === 'free') {
    return [
      {
        type: 'active',
      },
    ];
  }

  const result: FreePricingCardAction[] = [];

  if (workspace.type === 'personal') {
    const user = workspace.user;

    const hasSubscription = (user?.membership?.tier ?? 0) > 0;
    const hasCanceledSubscription = !!user && hasSubscription && user.membership?.subscription?.status === 'canceled';

    const isDowngraded =
      typeof user?.membership?.nextTier === 'number' && user.membership.nextTier < user.membership.tier;

    if (!hasSubscription) {
      return [
        {
          type: 'active',
        },
      ];
    }

    if (hasCanceledSubscription) {
      result.push({
        type: 'renew-subscription',
        onRenew: () => freePlanStore.renewSubscription(workspace),
      });
    }

    if (hasSubscription && !hasCanceledSubscription) {
      result.push({
        type: 'downgrade',
        onDowngrade: () => {
          pricingStore.showDowngradeModalData.set({
            isPersonal: true,
          });
        },
        disabilityReason: isDowngraded
          ? 'Your subscription is being downgraded. You need to cancel downgrade first'
          : undefined,
      });
    }
  }

  return result;
}

type DowngradeAction = {
  type: 'downgrade';
  onDowngrade: VoidFunction;
  disabilityReason?: string;
};

type DisabledAction = {
  type: 'disabled';
  reason: string;
};

type ActiveAction = {
  type: 'active';
};

type RenewSubscriptionAction = {
  type: 'renew-subscription';
  onRenew: VoidFunction;
};

export type FreePricingCardAction = DowngradeAction | DisabledAction | ActiveAction | RenewSubscriptionAction;
